import { useEffect, useMemo } from "react"
import { useQuery } from "urql"

import { type ProductConditions } from "../types"

import type { ProductConditionsQueryVariables } from "@/graphql/codegen/graphql"
import { ProductConditionsDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseConditionsProps {
  productId: number
  version?: number
}

interface UseConditionsResponse {
  data: ProductConditions | null
  fetching: boolean
  fetch: (variables: ProductConditionsQueryVariables) => void
}

export const useProductConditions = ({ productId, version }: UseConditionsProps): UseConditionsResponse => {
  const [{ data, error, fetching }, fetch] = useQuery({
    query: ProductConditionsDocument,
    variables: {
      productId,
      version,
    },
  })

  const hasError = error?.message || data?.product.get.__typename === "ProductGetFailure"
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  useEffect(() => {
    if (hasError) {
      notify({
        message: "Error fetching product conditions",
        type: NotificationType.Error,
        silent: false,
      })
    }
  }, [hasError, notify])

  const conditions = useMemo(() => {
    if (data?.product.get.__typename === "ProductGetSuccess") {
      return data.product.get.product
    }
    return null
  }, [data])

  return {
    data: conditions as ProductConditions | null,
    fetching,
    fetch,
  }
}
